import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { companyApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Palette, FileText, Eye, Save, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PdfCustomizerPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  
  const [config, setConfig] = useState({
    primary_color: "#5B9BD5",
    secondary_color: "#333333",
    show_logo: true,
    footer_text: "Gracias por su preferencia",
    show_qr: true,
    paper_size: "letter",
    font_family: "Inter"
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const companyRes = await companyApi.getMyCompany();
      setCompany(companyRes.data);
      
      // Load existing PDF config if any
      if (companyRes.data.pdf_config) {
        setConfig({ ...config, ...companyRes.data.pdf_config });
      }
      
      // Generate initial preview
      generatePreview(companyRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const generatePreview = async (companyData = company) => {
    // Mock invoice data for preview
    const mockInvoice = {
      series: "A",
      folio: "000001",
      cfdi_uuid: "ABC12345-1234-1234-1234-123456789012",
      subtotal: 10000,
      iva: 1600,
      total: 11600,
      payment_method: "PUE",
      payment_form: "03",
      currency: "MXN",
      created_at: new Date().toISOString(),
      items: [
        { 
          description: "Servicio de consultoría profesional", 
          quantity: 10, 
          unit_price: 1000,
          unit: "HRS",
          clave_producto: "80101500"
        }
      ]
    };

    const mockClient = {
      name: "Cliente Ejemplo",
      razon_social: "CLIENTE EJEMPLO SA DE CV",
      rfc: "CEX010101000",
      regimen_fiscal: "601",
      codigo_postal: "06600",
      uso_cfdi: "G03"
    };

    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.post(`${API}/invoices/preview-pdf`, {
        invoice_data: mockInvoice,
        client_data: mockClient,
        pdf_config: config
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      setPreviewHtml(response.data.html);
    } catch (error) {
      console.error("Error generating preview:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companyApi.updateCompany({ pdf_config: config });
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pdf-customizer-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Palette className="w-7 h-7 text-[#5B9BD5]" />
            Personalizar PDF
          </h1>
          <p className="text-slate-600">Personaliza el diseño de tus facturas PDF</p>
        </div>
        <Button onClick={handleSave} className="cia-btn-primary" disabled={saving} data-testid="save-config-btn">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
          Guardar Cambios
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <div className="space-y-6">
          <Card className="cia-card">
            <CardHeader>
              <CardTitle className="text-lg">Colores y Estilo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Color Principal</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={config.primary_color}
                      onChange={(e) => handleConfigChange("primary_color", e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={config.primary_color}
                      onChange={(e) => handleConfigChange("primary_color", e.target.value)}
                      className="cia-input flex-1"
                      data-testid="primary-color-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Color Secundario</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={config.secondary_color}
                      onChange={(e) => handleConfigChange("secondary_color", e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={config.secondary_color}
                      onChange={(e) => handleConfigChange("secondary_color", e.target.value)}
                      className="cia-input flex-1"
                      data-testid="secondary-color-input"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Fuente</Label>
                <select
                  value={config.font_family}
                  onChange={(e) => handleConfigChange("font_family", e.target.value)}
                  className="w-full p-2 border border-slate-200 rounded-md"
                >
                  <option value="Inter">Inter (Moderna)</option>
                  <option value="Arial">Arial (Clásica)</option>
                  <option value="Times New Roman">Times New Roman (Formal)</option>
                  <option value="Helvetica">Helvetica (Limpia)</option>
                </select>
              </div>
            </CardContent>
          </Card>

          <Card className="cia-card">
            <CardHeader>
              <CardTitle className="text-lg">Contenido</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="show_logo"
                  checked={config.show_logo}
                  onChange={(e) => handleConfigChange("show_logo", e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="show_logo">Mostrar logo de la empresa</Label>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="show_qr"
                  checked={config.show_qr}
                  onChange={(e) => handleConfigChange("show_qr", e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="show_qr">Mostrar código QR del SAT</Label>
              </div>

              <div className="space-y-2">
                <Label>Texto del pie de página</Label>
                <Textarea
                  value={config.footer_text}
                  onChange={(e) => handleConfigChange("footer_text", e.target.value)}
                  placeholder="Gracias por su preferencia"
                  className="cia-input"
                  rows={2}
                  data-testid="footer-text-input"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="cia-card">
            <CardHeader>
              <CardTitle className="text-lg">Tamaño de Papel</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: "letter", label: "Carta (Letter)", desc: "8.5\" x 11\"" },
                  { value: "a4", label: "A4", desc: "210mm x 297mm" }
                ].map((paper) => (
                  <button
                    key={paper.value}
                    onClick={() => handleConfigChange("paper_size", paper.value)}
                    className={`p-4 border rounded-lg text-left transition-all ${
                      config.paper_size === paper.value
                        ? "border-[#5B9BD5] bg-[#5B9BD5]/5"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="font-medium">{paper.label}</div>
                    <div className="text-sm text-slate-500">{paper.desc}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => generatePreview()}
            data-testid="preview-btn"
          >
            <Eye className="w-5 h-5 mr-2" />
            Actualizar Vista Previa
          </Button>
        </div>

        {/* Preview Panel */}
        <div className="space-y-4">
          <Card className="cia-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#5B9BD5]" />
                Vista Previa
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-inner"
                style={{ height: "600px" }}
              >
                {previewHtml ? (
                  <iframe
                    srcDoc={previewHtml}
                    className="w-full h-full"
                    title="PDF Preview"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-400">
                    <div className="text-center">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Haz clic en "Actualizar Vista Previa"</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <p className="text-sm text-slate-500 text-center">
            Esta es una vista previa aproximada. El PDF final puede variar ligeramente.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PdfCustomizerPage;
